import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import appDisplay from 'scoreboard/components/app-display'
import EditForm from 'scoreboard/components/edit/edit-form'
import FormView from 'scoreboard/components/form-view'
import globals from 'scoreboard/components/globals'
import ObjectSelectorForm from 'scoreboard/components/object-selector-form'
import newChartOptionsMenu from './new-chart-options-menu.hbs'
import ChartDesignerEditor, {
	IGNORE_CHANGE_SETTINGS
} from './chart-designer-editor'
import ChartsView from '../charts-view'
import chartsHeader from '../charts-header'
import chartsTabsTemplate from '../charts-tabs.hbs'
import conductor from 'scoreboard/components/conductor'
import CHART_TYPES from '../chart-types'
import SCORECARD_CHART_LAYOUTS from '../scorecard-chart-layouts'
import i18n from 'scoreboard/components/i18n'
import Tooltip from 'scoreboard/components/tooltip'
import updatesNeeded from './updates-needed'
import license from 'scoreboard/components/license'
import testScorecardItemSerie from './serie/test-scorecard-item-serie'
import { triggerAddOrEdit } from 'scoreboard/components/domain-object-events'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import { isV6 } from 'scoreboard/components/version'

const ChartsEdit = ChartsView.extend({

	className: 'display-layout table-hack charts-edit form',

	subsection: 'edit',

	events: {
		'click .cancel': 'onClickCancel',
		'click .tiles li': 'onLayoutSelected'
	},

	initialize: function(options = {}) {
		ChartsView.prototype.initialize.apply(this, arguments)

		this.setupForm()
		this.newChartDesignerEditor()
		// By default, the blank chart option should be selected
		this.layoutType = 'blank'
		this.listenTo(this.model, 'change', () => {
			// #41105 have to call updatesNeeded to skip dirtying the form
			// for attributes that don't represent a change (e.g. ids)
			const {chartDesigner} = this.model.attributes
			const {chartDesigner: previousChartDesigner} = this.model.previousAttributes()
			let {rerender} = updatesNeeded(chartDesigner, previousChartDesigner,
				IGNORE_CHANGE_SETTINGS)
			// #40361 because of the way this form is setup, we need to do this
			// to reset the form view so the save button enables properly after
			if (rerender) {
				this.formView.clearErrors()
				this.formView.change()
			}
		})
	},

	/**
	 * @override
	 */
	errorToDisplay () {
		// On the edit screen, errors on things like breakdowns and features
		// show up on the specific elements in the editor panels. So only show
		// top-level errors on the chart itself
		return this.model.chartDesigner.get('error')
	},

	setupForm: function() {
		// #42448 Pre-fill "New Chart" for the name of new charts
		if (this.model.isNew()) {
			this.model.set('label', i18n.get('charts.chart.add'))
		}

		this.editForm = new EditForm({
			model: this.model,
			section: 'charts',
			advancedLayoutOptions: this.model.isNew() ?
				$(newChartOptionsMenu(filteredChartTypes())) : null,
			// TODO: Remove when V5 is no longer supported
			// Should just be true
			hideFormContent: isV6() ? true : false
		})

		if (!this.formView) {
			this.formView = new FormView({
				toggleCancel: true,
				el: this.el,
				submit: this.onClickSave.bind(this),
				test: () => this.test(),
				primaryButtonEl: () => {
					return this.$('.button-primary.save')
				},
				// #42448 allow clean submission for new charts, since the name
				// is pre-filled
				allowCleanSubmission: () => this.model.isNew()
			})
			this.formView.on('change', this.onChangeLabel.bind(this))
		}

		this.editForm.on('save', () => this.formView.submit())
		this.editForm.on('cancel', this.onClickCancel.bind(this))
		this.editForm.on('deleted', () => {
			this.destroyChart()
		})
	},

	newChartDesignerEditor: function() {
		if (!_.isNil(this.chartDesignerEdit)) {
			this.chartDesignerEdit.remove()
			delete this.chartDesignerEdit
		}

		this.chartDesignerEdit = new ChartDesignerEditor({
			model: this.model.chartDesigner,
			data: this.data
		})

		this.chartDesignerEdit.on('rerenderChart', () => this.rerender(true))
		this.chartDesignerEdit.on('emphasize', id => this.renderChart(undefined, id))

		this.listenTo(this.chartDesignerEdit, 'change', (fetchRerender) => {
			this.onChange(fetchRerender)
		})

		this.listenTo(this.chartDesignerEdit, 'loading', () => this.toggleLoading(true))

		this.on('resize', () => this.chartDesignerEdit.trigger('resize'))
	},

	/**
	 * @override
	 */
	fetchOptions: function () {
		return {
			chartId: this.model.id,
			chart: this.model.chartDesigner
		}
	},

	onChange: function ({ fetch, rerender }) {
		this.formView.setDirty()

		if (fetch) {
			this.fetchAndRender(this.fetchOptions(), () => {
				this.rerender(true)
			})
		} else if (rerender) {
			this.rerender(true)
		}
	},

	onLayoutSelected: function(e) {
		const target = $(e.currentTarget)
		const type = target.data('type')
		const layoutOptions = CHART_TYPES
			.flatMap(c => c.options)
			.find(c => c.type === type)
		if (layoutOptions.disabled) {
			// If the layout type is not yet support, don't let it become selected
			this.tooltip = new Tooltip({
				attachTo: target,
				content: i18n.get('charts.chart.layoutNotSupported'),
				placement: 'above',
				arrow: 'center'
			}).render()
			return
		}

		// Update the selected styling
		this.$(`[data-type="${this.layoutType}"]`).toggleClass('is-selected', false)
		target.toggleClass('is-selected', true)
		this.layoutType = type

		// For scorecard options, the next step is choosing an item; otherwise
		// this is the final step and the chart is created
		let saveButtonLabel = _.includes(SCORECARD_CHART_LAYOUTS, type)
			? 'charts.chart.chooseItem' : 'common.create'
		this.$('.button.save').text(i18n.get(saveButtonLabel))
	},

	rerender: function (renderChart) {
		if (this.newChartCreated) {
			// Remove footer edit buttons
			this.$('.sticky-footer').remove()
			this.newChartDesignerEditor()
			this.renderSubsection(this.$('.scroll-container'))
			this.newChartCreated = false
		} else {
			this.chartDesignerEdit.refreshPanels()
		}
		this.renderChartAndAccessories(renderChart)
	},

	onChangeLabel: function () {
		// potentially update the "New Chart" placeholder in the tree
		this.model.set('label', this.editForm.val())
		this.trigger('change', this.model.toJSON())
	},

	renderSubsection: function(container) {
		container.empty()
		this.setupForm()
		container.append('<div class="banner banner-error"></div>')
			.append(this.editForm.el)
			.append(this.chartDesignerEdit.el)

		this.editForm.render()
		this.chartDesignerEdit.render()

		this.updateSectionVisibility()

		// Highlight the default option
		this.$(`[data-type="${this.layoutType}"`).toggleClass('is-selected', true)

		this.formView.start({
			isNew: this.model.isNew(),
			primaryButtonEl: this.$('.save')
		})

		let labelInput = this.$('[name=label]')

		labelInput.select()

		if (isV6()) {
			this.markedField = new MarkedField({
				el: labelInput,
				validator: requiredValidator
			}).render()
		}

		this.model.isNew() && labelInput.select()

		return this
	},

	updateHeader: function() {
		this.header.updateOptions({
			heading: chartsHeader.headerText(this.model),
			tabs: chartsTabsTemplate(this.model.toJSON())
		})
		this.header.render()
	},

	updateSectionVisibility: function () {
		this.chartDesignerEdit.$el.toggleClass('is-not-displayed', this.model.isNew())
	},

	test: function() {
		if (this.model.chartDesigner.get('plotType') === 'tree-map') {
			const serie = this.model.chartDesigner.getFeatures({ type: 'serie' })[0]
			// #40361 for tree maps, the rollup field is required when an actual
			// dataset is selected
			const domainType = serie.get('source').domainType
			if (domainType === 'dataset' && !serie.get('datasetRollupFieldId')) {
				return i18n.get('charts.chart.rollupFieldRequired')
			}
		}
	},

	onClickSave: function () {
		let isNew = this.model.isNew()
		this.model.set('label', this.editForm.val())
		this.model.set('layout', this.layoutType)

		if (isNew && _.includes(SCORECARD_CHART_LAYOUTS, this.layoutType)) {
			this.openScorecardItemSelector()
		} else {
			return this.submit()
		}
	},

	openScorecardItemSelector: function () {
		let objectSelectorForm = new ObjectSelectorForm({
			title: i18n.get('dashboards.choose.chart.scorecardNode'),
			orgId: this.model.get('organizationId'),
			section: 'scorecards',
			objectParams: {
				scoringType: true,
			},
			primaryButtonKey: 'common.create',
			testSelectedObject: testScorecardItemSerie
		})

		objectSelectorForm.on('select', item => {
			this.submit({ scorecardItemId: item.id }).then(() => {
				objectSelectorForm.trigger('done')
			})
		})

		objectSelectorForm.on('cancel', () => {
			// If the dialog is cancelled, this gets the underlying form back
			// to its previous state
			this.formView.resetForm({isNew: true, skipDoneAnimation: true})
			this.formView.change()
		})

		appDisplay.dialog(objectSelectorForm)
	},

	submit: function (layoutSettings) {
		let isNew = this.model.isNew()
		this.model.set('label', this.editForm.val())
		this.model.set('layoutSettings', _.extend({
			layout: this.layoutType
		}, layoutSettings))
		this.model.set('calendarPeriodId', globals.periodId)

		return $.ajax(this.model.url(), {
			method: isNew ? 'POST' : 'PATCH',
			data: JSON.stringify(this.model.toPostJSON()),
			success: response => {
				setNewIdsOnChart(this.model, response)

				// #38507 set initial attributes on a new chart designer
				if (isNew) {
					this.model.data.set(response.chartData)

					this.model.chartDesigner.set(response.chartDesigner)
					// #38854 update the header label and tabs
					this.updateHeader()
					this.newChartCreated = true
				} else if (response.chartDesigner.plotType) {
					// #38510 only update the plotType if one is actually provided
					// #38372 make sure the plot type is set in the correct place so
					// the chart designer knows what type of charts to allow
					this.model.chartDesigner.set('plotType', response.chartDesigner.plotType)
				}
				this.trigger('save', this.model)

				let action = isNew ? 'add' : 'edit'
				triggerAddOrEdit(action, this.model)

				this.updateSectionVisibility()

				this.formView.start({ isNew: false })
			}
		})
	},

	onClickCancel: function() {
		if (this.model.isNew()) {
			if (conductor.id()) {
				conductor.edit()
			} else {
				let url = `organizations/${this.model.get('organizationId')}/charts`
				conductor.navigate(url, { trigger: true })
			}
		} else {
			this.newChartCreated = true
			this.chartDesignerEdit.remove()
			// Even though we could keep the old model around, we'd still need fresh data
			// from the server in case the period was changed since the original render.
			// So for now, just do what's easy and reload the whole thing
			conductor.reload()
		}
	},

	remove: function() {
		ChartsView.prototype.remove.apply(this, arguments)
		_.invokeMap([ this.chartDesignerEdit, this.editForm,
			this.formView, this.tooltip ], 'remove')
	}
})

/**
 * @param {Chart} chart
 * @param {Object} response
 * For all newly created objects (like series and breakdowns), get the
 *     generated ID from the server response and set it on the matching model
 *     (using the tempId)
 */
function setNewIdsOnChart(chart, response) {
	if (chart.isNew()) {
		chart.set('id', response.id)
	}
	setNewIdsOnChartDesigner(chart.chartDesigner, response)
}

/**
 * @param {ChartDesigner} chartDesigner
 * @param {Object} response
 * Like the above "setNewIdsOnChart" but only for the chart designer
 */
function setNewIdsOnChartDesigner(chartDesigner, response) {
	// For all newly created objects (like series and breakdowns),
	// get the generated ID from the server response and set it
	// on the matching model (using the tempId)
	if (chartDesigner.isNew()) {
		chartDesigner.set('id', response.chartDesigner.id)
	}

	function setRealIds (models, responseObjects, extraSetter) {
		models.filter(m => !m.id).forEach(model => {
			let responseObject = _.find(responseObjects,
				object => object.tempId == model.get('tempId'))

			model.set('id', responseObject.id)

			extraSetter && extraSetter(model, responseObject)
		})
	}

	// #36721 Make sure each feature's forecast model has the correct id
	chartDesigner.getFeatures().forEach(feature => {
		const responseObject = _.find(response.chartDesigner.features,
			object => object.tempId == feature.get('tempId'))
		const forecastModel = feature.get('forecastModel')
		if (forecastModel) {
			forecastModel.id = responseObject.forecastModelId
			feature.set('forecastModel', forecastModel)
		}
	})

	// #47142 note that we don't replace the tempIds in chartDesigner.data.plots()
	// with the "real" IDs, because that's what we send to the chart drawing code.
	// If we change those IDs, it will think they are totally new series/ranges/etc,
	// and it will mess up the transitions.
	setRealIds(chartDesigner.getFeatures(), response.chartDesigner.features)
	setRealIds(chartDesigner.breakdowns, response.chartDesigner.breakdowns, (breakdown, responseBreakdown) => {
		if (breakdown.sortSettings && responseBreakdown.sortSettings) {
			breakdown.sortSettings.set('id', responseBreakdown.sortSettings.id)
		}
	})

	let allResponseRanges = _.flatMap(response.chartDesigner.breakdowns, b => {
		return b.ranges || []
	})

	chartDesigner.breakdowns.forEach(b => {
		setRealIds(b.ranges, allResponseRanges)
	})

	let breakdownIdsByTempId = _(chartDesigner.breakdowns.toArray())
		.map(b => [ b.get('tempId'), b.get('id') ])
		.fromPairs()
		.value()

	let rangesByTempId = _(chartDesigner.breakdowns.toArray())
		.flatMap(breakdown => {
			return breakdown.ranges.filter(r => !_.isNil(r.get('tempId')))
		})
		.keyBy(r => r.get('tempId'))
		.value()

	chartDesigner.getFeatures({ type: 'serie' }).forEach(serie => {
		let responseSerie = _.find(response.chartDesigner.features,
			respSerie => respSerie.id === serie.id)

		if (serie.domainType() === 'dataset' && !serie.get('source').aggregation.id) {
			serie.get('source').aggregation.id = responseSerie.source.aggregation.id
		}

		if (_.includes(['scorecard-item', 'initiative'], serie.domainType())
			&& !serie.get('source').id) {

			serie.get('source').id = responseSerie.source.id
		}

		// Though the serie's periods don't have their own id/tempId, they do
		// reference breakdown ranges that could have had a newly-created ID
		serie.periods.filter(period => _.isNil(period.breakdownRangeId))
			.forEach(period => {
				let range = rangesByTempId[period.get('breakdownRangeTempId')]
				if (range) {
					period.set('breakdownRangeId', range.id)
				}
			})
	})

	function updateSerieSortSettings (settings) {
		if (!settings) {
			return
		}

		_.forEach(settings.get('sortByValueRanges'), sortBy => {
			if (_.isNil(sortBy.breakdownId) && !_.isNil(sortBy.breakdownTempId)) {
				sortBy.breakdownId = breakdownIdsByTempId[sortBy.breakdownTempId]
			}

			if (_.isNil(sortBy.rangeId) && !_.isNil(sortBy.rangeTempId)) {
				let range = rangesByTempId[sortBy.rangeTempId]
				if (range) {
					sortBy.rangeId = range.get('id')
				}
			}
		})
	}

	updateSerieSortSettings(chartDesigner.leftAxisBarSortSettings)
	updateSerieSortSettings(chartDesigner.rightAxisBarSortSettings)
}

/**
 * returns the available CHART_TYPES, with types filtered out due to
 * license restrictions
 */
function filteredChartTypes() {
	let chartTypes = _.cloneDeep(CHART_TYPES)
	// #41880 hide Treemap when not licensed for datasets
	if (!license.datasets) {
		chartTypes.forEach(group => {
			group.options = _.filter(group.options, o => o.type != 'tree-map')
		})
	}
	return chartTypes
}

export default ChartsEdit
export { setNewIdsOnChartDesigner }
export { filteredChartTypes }


ChartsEdit.__filename__ = 'charts/edit/charts-edit.js'
